import { useIntl } from 'react-intl'
import PubSub from 'pubsub-js'
import { pick } from 'utilities/object'
import { isNil } from 'lodash'
import {
    revisionStatusLabels, revisionStatusClassNames,
    revisionGroupStatusLabels, revisionGroupStatusClassNames,
    periodUnits, defaultAmounts
} from 'pages/salary/constants'

export const useRevisionStatus = () => {
    const { formatMessage } = useIntl()

    return status => {
        if(!status) {
            return null
        }

        status = status.toLowerCase()

        const label = revisionStatusLabels[status] ?? null

        return {
            name: status,
            className: revisionStatusClassNames[status] ?? 'neutral',
            label: label ?
                formatMessage(label)
                : null
        }
    }
}

export const useRevisionGroupStatus = () => {
    const { formatMessage } = useIntl()

    return status => {
        if(!status) {
            return null
        }

        status = status.toLowerCase()

        const label = revisionGroupStatusLabels[status] ?? null

        return {
            name: status,
            className: revisionGroupStatusClassNames[status] ?? 'neutral',
            label: label ?
                formatMessage(label)
                : null
        }
    }
}

export const enrichRevisionGroup = (group = {}) => {
    const {
        users = [],
        revision = {}
    } = group

    const { hoursPerWeek } = revision

    if(!users.length || !revision) {
        return group
    }

    const immutableDefaultAmounts = { ...defaultAmounts }

    return {
        ...group,
        users: users.map(user => {
            const { currentSalary, newSalary } = user

            if(!newSalary) {
                return user
            }

            const currentNormalizedAmount = !!currentSalary ?
                getNormalizedAmount({
                    amount: currentSalary.amount,
                    periodUnit: currentSalary.periodUnit,
                    exchangeRate: getExchangeRate({
                        currency: currentSalary.currency,
                        exchangeRates: group.revision.exchangeRates,
                        revisionCurrency: group.revision.currency
                    }),
                    hoursPerWeek
                })
                : immutableDefaultAmounts

            const newNormalizedAmount = getNormalizedAmount({
                amount: newSalary.amount,
                periodUnit: newSalary.periodUnit,
                exchangeRate: getExchangeRate({
                    currency: newSalary.currency,
                    exchangeRates: group.revision.exchangeRates,
                    revisionCurrency: group.revision.currency
                }),
                hoursPerWeek
            })

            const currentAmount = !!currentSalary ?
                getAmount({
                    amount: currentSalary.amount,
                    periodUnit: currentSalary.periodUnit,
                    hoursPerWeek
                })
                : immutableDefaultAmounts

            const newAmount = getAmount({
                amount: newSalary.amount,
                periodUnit: newSalary.periodUnit,
                hoursPerWeek
            })

            const change = {
                percentage: currentSalary ?
                    (((newAmount.yearly - currentAmount.yearly) / currentAmount.yearly * 100) ?? 0)
                    : 0,
                fixed: periodUnits.reduce((accumulator, periodUnit) => ({
                    ...accumulator,
                    [periodUnit]: newAmount[periodUnit] - currentAmount[periodUnit] ?? 0
                }), {})
            }

            return {
                ...user,
                currentSalary: {
                    ...currentSalary,
                    amount: currentAmount,
                    normalizedAmount: currentNormalizedAmount,
                    ...(!currentSalary ?
                        pick(newSalary, 'currency', 'periodUnit')
                        : null
                    )
                },
                newSalary: {
                    ...newSalary,
                    amount: newAmount,
                    normalizedAmount: newNormalizedAmount
                },
                change
            }
        })
    }
}

export const getPeriodUnitConversion = hoursPerWeek => ({
    yearly: 1,
    monthly: 12,
    weekly: 52,
    hourly: 52 * hoursPerWeek
})

export const getExchangeRate = ({ currency, exchangeRates, revisionCurrency }) => {
    return revisionCurrency === currency ?
        1
        : exchangeRates?.[currency] ?? 1
}

export const getAmount = ({ amount, periodUnit = 'yearly', hoursPerWeek }) => {
    if(typeof amount === 'object') {
        amount = amount[periodUnit]
    } else if(periodUnit !== 'yearly') {
        amount = amount * getPeriodUnitConversion(hoursPerWeek)[periodUnit]
    }

    return {
        yearly: amount,
        monthly: amount / 12,
        weekly: amount / 52,
        hourly: amount / (52 * hoursPerWeek)
    }
}

export const getNormalizedAmount = ({ amount, periodUnit = 'yearly', exchangeRate = 1, employmentPercentage = 100, includeEmploymentPercentage = false, hoursPerWeek, invertExchangeRateCalculation = false }) => {
    if(typeof amount === 'object') {
        amount = amount[periodUnit]
    } else if(periodUnit !== 'yearly') {
        amount = amount * getPeriodUnitConversion(hoursPerWeek)[periodUnit]
    }

    if(!amount) {
        return null
    }

    let normalizedAmount = !invertExchangeRateCalculation ?
        (amount / exchangeRate)
        : (amount * exchangeRate)

    if(!!includeEmploymentPercentage) {
        if(isNil(employmentPercentage)) {
            employmentPercentage = 100
        }

        normalizedAmount = normalizedAmount * (employmentPercentage / 100)
    }

    return {
        yearly: normalizedAmount,
        monthly: normalizedAmount / 12,
        weekly: normalizedAmount / 52,
        hourly: normalizedAmount / (52 * hoursPerWeek)
    }
}

export const getUnnormalizedAmount = ({ normalizedAmount, periodUnit = 'yearly', exchangeRate = 1, hoursPerWeek }) => {
    if(typeof normalizedAmount === 'object') {
        normalizedAmount = normalizedAmount[periodUnit]
    } else if(periodUnit !== 'yearly') {
        normalizedAmount = normalizedAmount * getPeriodUnitConversion(hoursPerWeek)[periodUnit]
    }

    if(!normalizedAmount) {
        return null
    }

    return normalizedAmount * exchangeRate
}

export const getPeriodUnit = country => ({
    SE: 'monthly',
    FI: 'monthly'
})[country] ?? 'yearly'

export const refreshSalaryRevision = () => PubSub.publish('salaryRevision.refresh')
export const refreshSalaryRevisions = () => PubSub.publish('salaryRevisions.refresh')
export const refreshSalaryRevisionGroups = () => PubSub.publish('salaryRevisionGroups.refresh')
export const refreshSalaryRevisionGroup = () => PubSub.publish('salaryRevisionGroup.refresh')